import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  Add,
  Refresh,
  FilterList,
  UploadFile,
  PictureAsPdf,
  RemoveDone,
  Description as ExcelIcon,
  Assessment as CsvIcon,
  EventBusy as EventBusyIcon,
} from "@mui/icons-material";
import AddTaskIcon from "@mui/icons-material/AddTask";
import UpdateDisabledIcon from "@mui/icons-material/UpdateDisabled";
import {
  Fab as MuiFab,
  IconButton,
  Tooltip,
  Icon,
  CircularProgress,
  alpha,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import SyncIcon from "@mui/icons-material/Sync";

import Excel from "../vendor/icons/icon-excel.svg";
import Csv from "../vendor/icons/csv-file.png";
// Pdf logo switched to PictureAsPdf icon

const Fab = styled(MuiFab)(spacing);

function ToolBar({
  addPage,
  refreshPage,
  openFilterDialog,
  cancelSchedulePage,
  addSmsAndEmailPage,
  disconnectUserSession,
  bulkUser,
  bulkChangeValidity,
  downloadFile,
  isLoadingDownload,
  downloadFileType,
  isEnableImport,
  openDownloadDialog,
  isAddDisabled = false,
  approveOrRejectCAF,
  refreshPaymentStatus,
}) {
  const { t } = useTranslation();

  const renderDownload = (type, icon, color) => {
    const themeColor = color === "success" ? "#4caf50" : color === "info" ? "#2196f3" : "#f44336";

    return (
      <Tooltip title={t(type)}>
        {isLoadingDownload && downloadFileType === type ? (
          <CircularProgress size={20} sx={{ ml: 1 }} />
        ) : (
          <IconButton
            sx={{
              ml: 1,
              p: 1.2,
              borderRadius: "12px",
              bgcolor: alpha(themeColor, 0.08),
              color: themeColor,
              transition: "all 0.2s ease-in-out",
              "& .MuiSvgIcon-root": { fontSize: 24 },
              "&:hover": {
                bgcolor: alpha(themeColor, 0.15),
                transform: "translateY(-3px)",
                boxShadow: `0 4px 12px ${alpha(themeColor, 0.2)}`,
              },
            }}
            onClick={() => downloadFile(type)}
          >
            {icon}
          </IconButton>
        )}
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
      {addPage && (
        <Tooltip title={t("Add")}>
          <Fab size="small" color="secondary" onClick={addPage} disabled={isAddDisabled}>
            <Add />
          </Fab>
        </Tooltip>
      )}

      {disconnectUserSession && (
        <Tooltip title={t("Disconnect Session")}>
          <Fab size="small" color="secondary" onClick={disconnectUserSession}>
            <RemoveDone />
          </Fab>
        </Tooltip>
      )}

      {bulkUser && (
        <Tooltip title={t("Bulk Renewal")}>
          <Fab size="small" color="secondary" onClick={bulkUser}>
            <AddTaskIcon />
          </Fab>
        </Tooltip>
      )}

      {bulkChangeValidity && (
        <Tooltip title={t("Bulk Validity")}>
          <Fab size="small" color="secondary" onClick={bulkChangeValidity}>
            <EventBusyIcon />
          </Fab>
        </Tooltip>
      )}

      {cancelSchedulePage && (
        <Tooltip title={t("Cancel User Schedule")}>
          <IconButton onClick={cancelSchedulePage}>
            <UpdateDisabledIcon />
          </IconButton>
        </Tooltip>
      )}

      {addSmsAndEmailPage && (
        <Tooltip title={t("Send Mail")}>
          <Fab size="small" color="secondary" onClick={addSmsAndEmailPage}>
            <Add />
          </Fab>
        </Tooltip>
      )}

      {refreshPage && (
        <Tooltip title={t("Refresh")}>
          <IconButton onClick={refreshPage}>
            <Refresh />
          </IconButton>
        </Tooltip>
      )}

      {openFilterDialog && (
        <Tooltip title={t("Filter")}>
          <IconButton onClick={openFilterDialog}>
            <FilterList />
          </IconButton>
        </Tooltip>
      )}

      {renderDownload("xlsx", <ExcelIcon />, "success")}
      {renderDownload("csv", <CsvIcon />, "info")}
      {renderDownload("pdf", <PictureAsPdf />, "error")}

      {isEnableImport && (
        <Tooltip title={t("Upload")}>
          <IconButton onClick={openDownloadDialog}>
            <UploadFile />
          </IconButton>
        </Tooltip>
      )}
      {approveOrRejectCAF && (
        <Tooltip title={t("Approve or Reject CAF")}>
          <Fab size="small" color="secondary" onClick={approveOrRejectCAF}>
            <AddTaskIcon />
          </Fab>
        </Tooltip>
      )}
      {refreshPaymentStatus && (
        <Tooltip title="Sync Payment Status">
          <Fab size="small" color="secondary" onClick={refreshPaymentStatus}>
            <SyncIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
}

export default ToolBar;
